import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../../src/functions/useLanguage';
import { wt } from '../../i18n';
import { websitePath } from '../../lib/routes';

const META = {
  title: 'The Care Circle: Coordinating Your Pet’s Health Team | KodaCare Blog',
  description:
    'Bring family, sitters, and your vet into one shared plan. Learn how KodaCare keeps meds, symptoms, and routines aligned.',
  slug: 'the-care-circle',
  dateISO: '2026-04-23',
  readMins: 6,
  tag: 'Roomies',
};

type Lang = 'English' | 'French' | 'German' | 'Portuguese' | 'Spanish';

const LOCALE_BY_LANGUAGE: Record<Lang, string> = {
  English: 'en-GB',
  French: 'fr-FR',
  German: 'de-DE',
  Portuguese: 'pt-PT',
  Spanish: 'es-ES',
};

export function TheCareCircle() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);
  const activeLanguage = language as Lang;

  const titleByLanguage: Record<string, string> = {
    English: 'The Care Circle: Coordinating Your Pet’s Health Team',
    French: 'Le cercle de soins : coordonner l’équipe de santé de votre animal',
    German: 'Der Pflegekreis: Das Gesundheitsteam Ihres Haustiers koordinieren',
    Portuguese: 'O círculo de cuidados: coordenar a equipa de saúde do seu animal',
    Spanish: 'El círculo de cuidado: coordinar el equipo de salud de tu mascota',
  };

  const descriptionByLanguage: Record<Lang, string> = {
    English:
      'Bring family, sitters, and your vet into one shared plan. Learn how KodaCare keeps meds, symptoms, and routines aligned.',
    French:
      'Impliquez famille, pet sitters et vétérinaire dans un plan commun. KodaCare aligne traitements, symptômes et routines.',
    German:
      'Binden Sie Familie, Sitter und Tierarzt in einen gemeinsamen Plan ein. KodaCare hält Medikation, Symptome und Routinen synchron.',
    Portuguese:
      'Inclua família, cuidadores e veterinário num plano único. O KodaCare mantém medicação, sintomas e rotinas alinhados.',
    Spanish:
      'Incluye a familia, cuidadores y veterinario en un plan compartido. KodaCare alinea medicación, síntomas y rutinas.',
  };

  const localizedTitle = `${titleByLanguage[activeLanguage] ?? titleByLanguage.English} | KodaCare Blog`;
  const localizedDescription = descriptionByLanguage[activeLanguage] ?? descriptionByLanguage.English;

  const tagLabel =
    META.tag === 'Roomies'
      ? t('blog.tag.roomies')
      : META.tag;

  const isEnglishContent = activeLanguage === 'English';
  const translatedSummary: Record<Exclude<Lang, 'English'>, {
    intro: string;
    sections: { heading: string; paragraphs: string[] }[];
    closing: string;
    series: string;
  }> = {
    French: {
      intro:
        'Quand plusieurs personnes s’occupent d’un animal, les informations se perdent vite. Un cercle de soins partagé évite les oublis et les contradictions.',
      sections: [
        {
          heading: '1. Cartographier l’équipe de soins',
          paragraphs: [
            'Vétérinaire, famille, pet sitter, toiletteur: chacun a un rôle différent et des observations utiles.',
            'Clarifier qui surveille quoi permet d’agir plus vite quand un changement apparaît.'
          ],
        },
        {
          heading: '2. Un langage commun au quotidien',
          paragraphs: [
            'Des notes simples sur l’appétit, l’énergie, la respiration et les médicaments évitent les malentendus.',
            'KodaCare centralise les tendances pour que tout le monde voie la même histoire.'
          ],
        },
        {
          heading: '3. Des relais faciles',
          paragraphs: [
            'Un résumé hebdomadaire et des alertes claires facilitent la coordination entre proches et vétérinaire.',
            'L’objectif: moins de messages dispersés, plus de décisions nettes.'
          ],
        },
      ],
      closing: 'Un cercle de soins aligné protège votre animal et simplifie la vie de toute l’équipe.',
      series: 'KodaCare Journal · Article 2 sur 4',
    },
    German: {
      intro:
        'Wenn mehrere Personen ein Haustier betreuen, gehen wichtige Details schnell verloren. Ein gemeinsamer Pflegekreis schafft Klarheit.',
      sections: [
        {
          heading: '1. Das Team klar definieren',
          paragraphs: [
            'Tierarzt, Familie, Sitter und Groomer sehen unterschiedliche Dinge im Alltag.',
            'Je klarer die Rollen, desto schneller reagieren Sie auf Veränderungen.'
          ],
        },
        {
          heading: '2. Gemeinsame Signale teilen',
          paragraphs: [
            'Appetit, Energie, Atmung und Medikamente gehören in einen gemeinsamen Überblick.',
            'KodaCare bündelt diese Daten, damit alle dieselbe Entwicklung sehen.'
          ],
        },
        {
          heading: '3. Reibungslose Übergaben',
          paragraphs: [
            'Kurze Wochen-Updates und klare Markierungen sparen lange Nachrichtenketten.',
            'So wird aus vielen Stimmen eine klare Handlungsgrundlage.'
          ],
        },
      ],
      closing: 'Ein abgestimmter Pflegekreis schützt Ihr Tier und entlastet alle Beteiligten.',
      series: 'KodaCare Journal · Artikel 2 von 4',
    },
    Portuguese: {
      intro:
        'Quando várias pessoas cuidam do mesmo animal, a informação perde-se facilmente. Um círculo de cuidados partilhado evita falhas.',
      sections: [
        {
          heading: '1. Definir a equipa de cuidados',
          paragraphs: [
            'Veterinário, família, pet sitter e groomer observam partes diferentes da rotina.',
            'Papéis claros ajudam a agir rápido quando algo muda.'
          ],
        },
        {
          heading: '2. Sinais diários em comum',
          paragraphs: [
            'Apetite, energia, respiração e medicação precisam de um registo único.',
            'O KodaCare centraliza tendências para todos verem a mesma evolução.'
          ],
        },
        {
          heading: '3. Passagens simples',
          paragraphs: [
            'Resumos semanais e alertas claros reduzem mensagens dispersas.',
            'Assim, a equipa decide com mais rapidez e confiança.'
          ],
        },
      ],
      closing: 'Um círculo alinhado protege o seu animal e simplifica o dia a dia de todos.',
      series: 'KodaCare Journal · Artigo 2 de 4',
    },
    Spanish: {
      intro:
        'Cuando varias personas cuidan a una mascota, los detalles se pierden rápido. Un círculo de cuidado compartido evita vacíos.',
      sections: [
        {
          heading: '1. Definir el equipo de cuidado',
          paragraphs: [
            'Veterinario, familia, cuidadores y groomer ven cosas distintas en el día a día.',
            'Con roles claros, es más fácil actuar ante cualquier cambio.'
          ],
        },
        {
          heading: '2. Señales diarias en un solo lugar',
          paragraphs: [
            'Apetito, energía, respiración y medicación deben quedar en un registro común.',
            'KodaCare centraliza tendencias para que todos vean la misma historia.'
          ],
        },
        {
          heading: '3. Relevos sin fricción',
          paragraphs: [
            'Resúmenes semanales y alertas claras reducen cadenas de mensajes largas.',
            'El resultado: decisiones más rápidas y consistentes.'
          ],
        },
      ],
      closing: 'Un círculo de cuidado alineado protege a tu mascota y facilita el trabajo en equipo.',
      series: 'KodaCare Journal · Artículo 2 de 4',
    },
  };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = localizedTitle;

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.content;
    metaDesc.content = localizedDescription;

    const upsertMeta = (selector: string, attr: 'property' | 'name', value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, selector.match(/\[.+?="(.+?)"\]/)?.[1] ?? '');
        document.head.appendChild(el);
      }
      const previous = el.content;
      el.content = value;
      return { el, created, previous };
    };

    const updates = [
      upsertMeta('meta[property="og:title"]', 'property', localizedTitle),
      upsertMeta('meta[property="og:description"]', 'property', localizedDescription),
      upsertMeta('meta[property="og:type"]', 'property', 'article'),
      upsertMeta('meta[property="og:url"]', 'property', `https://kodacare.pet/blog/${META.slug}`),
      upsertMeta('meta[name="twitter:card"]', 'name', 'summary_large_image'),
      upsertMeta('meta[name="twitter:title"]', 'name', localizedTitle),
      upsertMeta('meta[name="twitter:description"]', 'name', localizedDescription),
      upsertMeta('meta[name="twitter:image"]', 'name', 'https://kodacare.pet/icons/og-image.png'),
      upsertMeta('meta[property="og:image"]', 'property', 'https://kodacare.pet/icons/og-image.png'),
    ];

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const canonicalCreated = !canonical;
    const previousCanonical = canonical?.href ?? '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://kodacare.pet/blog/${META.slug}`;

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.content = prevDesc;

      updates.forEach(({ el, created, previous }) => {
        if (created) {
          el.remove();
        } else {
          el.content = previous;
        }
      });

      if (canonical) {
        if (canonicalCreated) {
          canonical.remove();
        } else {
          canonical.href = previousCanonical;
        }
      }
    };
  }, [localizedDescription, localizedTitle]);

  return (
    <main className="min-h-screen bg-white">
      <div className="bg-[#0d2744] pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="flex items-center gap-2 text-primary-brown/80 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> {t('blog.backToBlog')}
          </button>
          <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-primary-brown/15 text-primary-brown mb-4">
            {tagLabel}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
            {titleByLanguage[language] ?? titleByLanguage.English}
          </h1>
          <div className="flex items-center gap-3 text-sm text-primary-brown/80">
            <span>{new Date(`${META.dateISO}T00:00:00`).toLocaleDateString(LOCALE_BY_LANGUAGE[activeLanguage] ?? 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span>·</span>
            <span>{META.readMins} {t('blog.minRead')}</span>
            <span>·</span>
            <span>{t('blog.authorTeam')}</span>
          </div>
        </div>
      </div>

      {!isEnglishContent && (
        <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">
          <p className="text-xl text-gray-600 leading-relaxed mb-8 not-prose">
            {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].intro}
          </p>
          {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].sections.map((section) => (
            <React.Fragment key={section.heading}>
              <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">{section.heading}</h2>
              {section.paragraphs.map((paragraph, idx) => (
                <p key={`${section.heading}-${idx}`}>{paragraph}</p>
              ))}
            </React.Fragment>
          ))}
          <p>{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].closing}</p>
          <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
            <p className="text-sm text-gray-600 italic leading-relaxed">
              <strong className="text-[#0d2744]">{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].series}</strong>
            </p>
          </div>
        </article>
      )}

      <article hidden={!isEnglishContent} className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">
        <p className="text-xl text-gray-600 leading-relaxed mb-8 not-prose">
          Caring for a pet is rarely a solo job. Between family members, pet sitters, groomers, and
          your veterinary team, important details can slip through the cracks. That’s why KodaCare
          encourages a shared care circle—one place where everyone sees the same story.
        </p>

        <p>
          When every caregiver uses different notes, the plan becomes fragmented. A shared log
          turns scattered observations into a single timeline. It’s the difference between “I think
          he skipped dinner” and “He ate 40% less for three days.”
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">1. Map the Care Circle</h2>

        <p>
          Start by listing the people who touch your pet’s daily routine. Your vet sets the care
          plan, but caregivers handle the execution. Everyone should know their role and what
          signals they’re responsible for watching.
        </p>

        <ul className="space-y-4 my-6 not-prose list-none pl-0">
          <li className="flex gap-3">
            <span className="mt-1 w-2 h-2 rounded-full bg-primary-brown shrink-0" />
            <span>
              <strong className="text-[#0d2744]">Family:</strong> meals, meds, bathroom habits, and
              day-to-day energy.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="mt-1 w-2 h-2 rounded-full bg-primary-brown shrink-0" />
            <span>
              <strong className="text-[#0d2744]">Sitters:</strong> appetite, walks, coughing,
              restlessness, and behavior changes.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="mt-1 w-2 h-2 rounded-full bg-primary-brown shrink-0" />
            <span>
              <strong className="text-[#0d2744]">Vet team:</strong> diagnostics, labs, and the care
              plan that guides everyone else.
            </span>
          </li>
        </ul>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">2. Share the Same Daily Signals</h2>

        <p>
          Consistency beats complexity. Track a few core signals daily: appetite, energy, breathing,
          mobility, and medications. Over time, this creates a trend line that helps your vet see
          what’s changing—and when.
        </p>

        <p>
          KodaCare keeps these signals in one place so that your partner, your sitter, and your vet
          are all looking at the same data. No guessing, no missing context.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">3. Use Simple Handoffs</h2>

        <p>
          Every handoff should answer three questions: What happened? What changed? What should we
          do next? A weekly summary plus a quick note after each shift keeps the care circle in
          sync.
        </p>

        <p>
          When it’s time for a vet visit, a clean summary of trends saves appointment time and gets
          you to answers faster.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">4. Plan for Urgent Moments</h2>

        <p>
          Agree ahead of time on red flags: persistent vomiting, labored breathing, sudden
          lethargy, or a missed critical dose. With KodaCare, those moments are documented and
          shared instantly, so the right person can act quickly.
        </p>

        <p>
          <strong>Aligned care protects your pet and relieves the people who love them.</strong>
        </p>

        <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
          <p className="text-sm text-gray-600 italic leading-relaxed">
            <strong className="text-[#0d2744]">KodaCare Journal · Article 2 of 4</strong>
            <br />
            This is the second article in the KodaCare Journal. Up next: The Lab Story and how to
            read your pet’s results without the panic.
          </p>
        </div>
      </article>

      <div className="border-t border-gray-100 py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-sm text-gray-500">{t('blog.moreFromTeam')}</p>
            <p className="font-bold text-[#0d2744] text-lg">{t('blog.readAllArticles')}</p>
          </div>
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="px-8 py-3 bg-[#0d2744] text-white rounded-xl font-bold hover:bg-[#1a3a5c] transition-colors"
          >
            ← {t('blog.backToBlog')}
          </button>
        </div>
      </div>
    </main>
  );
}
